import React, {useEffect, useState} from 'react'
import {fetchFormCompanyLogo, FormCompanyLogo as CompanyLogo} from '../../core/forms_requests'

export const resolveFormLogoUrl = (raw?: string | null): string | null => {
  const value = raw?.trim()
  if (!value) return null
  // Company uploads use /uploads/company-branding/... on the API host.
  if (/^https?:\/\//i.test(value)) return value
  if (!/^\/?uploads\//i.test(value)) return null
  const path = value.startsWith('/') ? value : `/${value}`
  const apiUrl = process.env.REACT_APP_API_URL || '/api'
  return new URL(path, new URL(apiUrl, window.location.origin)).href
}

export const FormCompanyLogoDisplay: React.FC<{
  company: CompanyLogo | null
  message?: string
  showPlaceholder?: boolean
  children?: React.ReactNode
}> = ({company, message, showPlaceholder = true, children}) => {
  const url = resolveFormLogoUrl(company?.logoUrl)
  const watermarkUrl = resolveFormLogoUrl(company?.watermarkUrl)
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  const [failedWatermarkUrl, setFailedWatermarkUrl] = useState<string | null>(null)
  const hasLogo = Boolean(url && url !== failedUrl)
  const hasWatermark = Boolean(watermarkUrl && watermarkUrl !== failedWatermarkUrl)
  const showHeader = hasLogo || showPlaceholder || Boolean(company?.companyName)

  return (
    <div className='form-company-branding' style={{position: 'relative', isolation: 'isolate'}}>
      {hasWatermark && (
        <div
          className='form-company-watermark'
          aria-hidden='true'
          data-html2canvas-ignore='true'
          style={{position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden'}}
        >
          <img
            key={watermarkUrl}
            src={watermarkUrl!}
            alt=''
            crossOrigin='anonymous'
            data-pdf-watermark-source='true'
            onError={() => setFailedWatermarkUrl(watermarkUrl)}
            style={{width: '56%', maxWidth: 520, maxHeight: '75%', objectFit: 'contain', opacity: 0.08, filter: 'grayscale(100%)', transform: 'translateY(-8%)'}}
          />
        </div>
      )}
      <div className='form-company-content' style={{position: 'relative', zIndex: 1}}>
        {showHeader && (
          <div className='d-flex align-items-center flex-wrap gap-3 mb-4 pb-3 border-bottom' style={{breakInside: 'avoid'}}>
            {hasLogo && (
              <img
                key={url}
                src={url!}
                alt={`${company?.companyName || 'Company'} logo`}
                crossOrigin='anonymous'
                onError={() => setFailedUrl(url)}
                style={{maxWidth: 'min(240px, 100%)', maxHeight: 64, objectFit: 'contain'}}
              />
            )}
            <div style={{minWidth: 0, overflowWrap: 'anywhere'}}>
              {company?.companyName && <div className='fw-semibold text-dark'>{company.companyName}</div>}
              {!hasLogo && showPlaceholder && (
                <div className='text-muted small' role='status'>
                  {message || (url === failedUrl && url ? 'Company logo unavailable.' : 'No company logo uploaded.')}
                </div>
              )}
            </div>
          </div>
        )}
        {children}
      </div>
    </div>
  )
}

const FormCompanyLogo: React.FC<{vesselId?: number | null; emptyMessage?: string; children?: React.ReactNode}> = ({vesselId, emptyMessage, children}) => {
  const [result, setResult] = useState<{
    vesselId: number
    company: CompanyLogo | null
    error?: boolean
  } | null>(null)

  useEffect(() => {
    if (!vesselId) return
    let active = true
    fetchFormCompanyLogo(vesselId)
      .then((company) => {
        if (active) setResult({vesselId, company})
      })
      .catch(() => {
        if (active) setResult({vesselId, company: null, error: true})
      })
    return () => {active = false}
  }, [vesselId])

  const current = result?.vesselId === vesselId ? result : null
  const message = !vesselId
    ? emptyMessage || 'Select a vessel to display its company logo.'
    : !current
    ? 'Loading company logo...'
    : current.error
    ? 'Company logo unavailable.'
    : !current.company?.companyId
    ? 'No company linked to this vessel.'
    : undefined

  return <FormCompanyLogoDisplay company={current?.company ?? null} message={message}>{children}</FormCompanyLogoDisplay>
}

export default FormCompanyLogo
